import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Clock, TrendingUp } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { searchSuggestions } from "@/services/catalog";

const HISTORY_KEY = "kha_search_history";
const POPULAR = ["Headphones", "Sneakers", "Serum", "Lamp", "Wallet", "Camera"];

const readHistory = () => {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY)) || []; } catch { return []; }
};

export default function SearchBar({ className = "" }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [history, setHistory] = useState(readHistory());
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setSuggestions([]); return; }
    timer.current = setTimeout(async () => {
      setSuggestions(await searchSuggestions(q.trim()));
    }, 220);
    return () => clearTimeout(timer.current);
  }, [q]);

  const submit = (term) => {
    const value = (term ?? q).trim();
    if (!value) return;
    const next = [value, ...history.filter((h) => h !== value)].slice(0, 6);
    setHistory(next);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
    setOpen(false);
    setQ(value);
    navigate(`/search?q=${encodeURIComponent(value)}`);
  };

  const clearHistory = () => { setHistory([]); localStorage.removeItem(HISTORY_KEY); };

  return (
    <div ref={boxRef} className={`relative ${className}`} data-testid="search-bar">
      <form
        onSubmit={(e) => { e.preventDefault(); submit(); }}
        className="flex h-11 items-center overflow-hidden rounded-full border border-border bg-background focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20"
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={t("search_placeholder")}
          className="h-full flex-1 bg-transparent px-4 text-sm outline-none placeholder:text-muted-foreground"
          data-testid="search-input"
        />
        {q && (
          <button type="button" onClick={() => setQ("")} className="px-2 text-muted-foreground" aria-label="clear">
            <X size={16} />
          </button>
        )}
        <button type="submit" className="mx-1 grid h-9 w-9 place-items-center rounded-full bg-brand text-brand-foreground transition hover:brightness-105" data-testid="search-submit" aria-label="search">
          <Search size={18} />
        </button>
      </form>

      {open && (
        <div className="absolute inset-x-0 top-full z-50 mt-2 overflow-hidden rounded-2xl border border-border bg-popover shadow-card fade-up" data-testid="search-dropdown">
          {suggestions.length > 0 && (
            <ul className="max-h-72 overflow-auto py-1">
              {suggestions.map((s) => (
                <li key={s.slug}>
                  <button onClick={() => { setOpen(false); navigate(`/product/${s.slug}`); }} className="flex w-full items-center gap-3 px-4 py-2.5 text-start text-sm hover:bg-secondary">
                    <Search size={15} className="text-muted-foreground" />
                    <span className="line-clamp-1">{s.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {suggestions.length === 0 && (
            <div className="p-4">
              {history.length > 0 && (
                <div className="mb-4">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"><Clock size={13} /> {t("recently_viewed")}</span>
                    <button onClick={clearHistory} className="text-xs text-brand hover:underline">{t("clear_all")}</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {history.map((h) => (
                      <button key={h} onClick={() => submit(h)} className="rounded-full bg-secondary px-3 py-1 text-xs hover:bg-secondary/70">{h}</button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <span className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"><TrendingUp size={13} /> {t("trending")}</span>
                <div className="flex flex-wrap gap-2">
                  {POPULAR.map((p) => (
                    <button key={p} onClick={() => submit(p)} className="rounded-full border border-border px-3 py-1 text-xs hover:border-brand hover:text-brand">{p}</button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
